import {
    AUTHORIZATION_HEADER_NAME,
    clearAuthTokens,
    getAccessToken,
    getRefreshToken,
    setAuthTokens,
} from 'src/utils/auth';

let isRedirecting = false;
let isRefreshing = false;

const BASE_URL =
  process.env.REACT_APP_DEPLOY_MODE === 'DEV'
    ? 'http://localhost:8000'
    : process.env.REACT_APP_BACKEND_URL || '/';

let controller: AbortController;
let signal: AbortSignal;

function _resetAbortController() {
    controller = new AbortController();
    signal = controller.signal;
}

// init controller and signal
_resetAbortController();

export function abortPreviousRequests(): void {
    controller.abort();

    // replace controller and signal so we have a new unaborted one (otherwise the
    // controller remains aborted and will abort any fetch using its signal immediately)
    _resetAbortController();
}

export function login(email: string, password: string): Promise<any> {
    return _callAPI('user/login', 'POST', { email, password });
}

export function changePassword(
    email: string,
    old_password: string,
    new_password: string
): Promise<any> {
    return _callAuthenticatedAPI('user/login/change-password', 'POST', {
        email,
        old_password,
        new_password
    });
}

export function verifyToken(token: string): Promise<any> {
    return _callAuthenticatedAPI('user/reset/verify', 'POST', {
        token
    });
}

export function resetPassword(token: string, password: string): Promise<any> {
    return _callAuthenticatedAPI('user/reset/confirm', 'POST', {
        token,
        password
    });
}

export function forgetPassword(email: string): Promise<any> {
    return _callAuthenticatedAPI('user/reset/request', 'POST', {
        email
    });
}

export function getUser(
    type: string,
    page: number,
    limit: number,
    search?: string
): Promise<any> {
    return _callAuthenticatedAPI(
        `user?type=${type}&page=${page}&limit=${limit}` +
            (search ? `&search=${search}` : '')
    );
}

export function getPatients(
    page: number,
    limit: number,
    search?: string,
    showSessions?: boolean
): Promise<any> {
    return _callAuthenticatedAPI(
        `scan/patients?page=${page}&results_per_page=${limit}` +
            (search ? `&search=${search}` : '') +
            (showSessions === false ? '&hide_sessions=true' : '')
    );
}

export function getPatientScans(
    patientId: string,
    page: number,
    limit: number,
    search?: string
): Promise<any> {
    // return _callAuthenticatedAPI(
    //     `scan/patient/${patientId}/scans?page=${page}&limit=${limit}` +
    //         (search ? `&search=${search}` : '')
    // );
    return getRawDataScans(page, limit, search)
}

export function getRawDataScans(
    page: number,
    limit: number,
    search?: string
): Promise<any> {
    // return _callAuthenticatedAPI(
    //     `scan/patient/${patientId}/scans?page=${page}&limit=${limit}` +
    //         (search ? `&search=${search}` : '')
    // );
    return _callAuthenticatedAPI(
        `scan/rawdata?page=${page}&limit=${limit}` +
        (search ? `&search=${encodeURIComponent(search)}` : '')
    );
}


export function getSubScans(patientId: string, scanId: string): Promise<any> {
    return _callAuthenticatedAPI(
        `scan/${scanId}/sub-scans`
    );
}

export function getSubScanPreview(
    patientId: string,
    scanId: string,
    mode: number,
    odos: number,
    scan: number
): Promise<any> {
    return _callAuthenticatedAPI(
        `scan/${scanId}/sub-scan/${mode}/${odos}/${scan}/preview`
    );
}

export function getSubScanTrace(
    patientId: string,
    scanId: string,
    mode: number,
    odos: number,
    scan: number
): Promise<any> {
    return _callAuthenticatedAPI(
        `scan/${scanId}/sub-scan/${mode}/${odos}/${scan}/trace`
    );
}

export function getSubScanSummary(
    patientId: string,
    scanId: string,
    mode: number,
    odos: number,
    scan: number
): Promise<any> {
    return _callAuthenticatedAPI(
        `scan/${scanId}/sub-scan/${mode}/${odos}/${scan}/summary`
    );
}

// legacy helpers removed; using presignUpload/submitJob/getJobStatus below

// ===== NEW API ENDPOINTS FOR RETINATRACK INTEGRATION =====

// Prompts API (from diana_infra branch)
export function getPrompts(): Promise<any> {
    return _callAuthenticatedAPI('prompts/', 'GET');
}

export function createPrompt(promptData: any): Promise<any> {
    return _callAuthenticatedAPI('prompts/', 'POST', promptData);
}

// Orchestrator API (from diana_infra branch)
export function runOrchestration(orchestrationData: any): Promise<any> {
    return _callAuthenticatedAPI('orchestrator/run', 'POST', orchestrationData);
}

export function classifyAndRoute(classificationData: any): Promise<any> {
    return _callAuthenticatedAPI('orchestrator/classify', 'POST', classificationData);
}

// Data API (for patient/visit data)
export function getDataById(id: string): Promise<any> {
    return _callAuthenticatedAPI(`data/${id}`, 'GET');
}

// Metrics API (for success rate and latency)
export function getMetrics(): Promise<any> {
    return _callAuthenticatedAPI('metrics', 'GET');
}

// Trace API (for session history)
export function getTrace(): Promise<any> {
    return _callAuthenticatedAPI('trace', 'GET');
}

// Intent Classification API (from mlclassificationbranch)
export function classifyIntent(intentData: any): Promise<any> {
    return _callAuthenticatedAPI('intent/classify', 'POST', intentData);
}

// ====== Scan/Patient APIs (backend branch) ======
// Note: exact payloads depend on backend. We keep types as any with graceful handling.
export function getScanPatients(): Promise<any> {
    return _callAuthenticatedAPI('scan/patients', 'GET');
}

// Patient Overview API (using orchestrator patient_overview agent)
export function getPatientOverview(patientId: string | number, options?: {
    include_metrics?: boolean;
    include_visits?: boolean;
    time_range?: string;
}): Promise<any> {
    return runOrchestration({
        request_id: `patient_overview_${patientId}_${Date.now()}`,
        intents: ['patient_overview'],
        entities: {
            patient_id: patientId,
            include_metrics: options?.include_metrics !== false,
            include_visits: options?.include_visits !== false,
            time_range: options?.time_range || 'all'
        }
    });
}

export function getScanSummary(scanId: string): Promise<any> {
    // Some backends expose summary via sub-scan path; keeping a simple path first.
    return _callAuthenticatedAPI(`scan/${scanId}/summary`, 'GET');
}

export function presignUpload(data: any): Promise<any> {
    const filename = data?.filename || data?.name || '';
    const fileSize = data?.file_size || data?.size || '';
    const qs = `filename=${encodeURIComponent(filename)}` + (fileSize ? `&file_size=${fileSize}` : '');
    return _callAuthenticatedAPI(`scan/presign-upload?${qs}`, 'GET');
}

export function submitJob(data: any): Promise<any> {
    return _callAuthenticatedAPI('scan/submit-job', 'POST', data);
}

export function getJobStatus(jobId: string): Promise<any> {
    return _callAuthenticatedAPI(`scan/jobs/${jobId}/`, 'GET');
}

export function getAnalysisJobs(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    reviewed?: string;
    search?: string;
    patient?: string;
}): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    if (params?.status) qs.set('status', params.status);
    if (params?.reviewed) qs.set('reviewed', params.reviewed);
    if (params?.search) qs.set('search', params.search);
    if (params?.patient) qs.set('patient', params.patient);
    const query = qs.toString();
    return _callAuthenticatedAPI(`scan/jobs/${query ? '?' + query : ''}`, 'GET');
}

export function getJobSummary(): Promise<any> {
    return _callAuthenticatedAPI('scan/jobs/summary/', 'GET');
}

export function getJobStats(): Promise<any> {
    return _callAuthenticatedAPI('scan/jobs/stats/', 'GET');
}

export function reviewJob(jobId: string, notes?: string): Promise<any> {
    return _callAuthenticatedAPI(`scan/jobs/${jobId}/review/`, 'PATCH', notes !== undefined ? { notes } : undefined);
}

export function updateJobNotes(jobId: string, notes: string): Promise<any> {
    return _callAuthenticatedAPI(`scan/jobs/${jobId}/notes/`, 'PATCH', { notes });
}

export function assignJobToPatient(
    jobId: string,
    patientId: number,
    options?: { forceReassign?: boolean },
): Promise<any> {
    const body: Record<string, unknown> = { patient_id: patientId };
    if (options?.forceReassign) body.force_reassign = true;
    return _callAuthenticatedAPI(`scan/jobs/${jobId}/assign-patient/`, 'PATCH', body);
}

export function bulkAssignPatient(
    jobIds: string[],
    patientId?: number,
    newSubjectId?: string,
    options?: { forceReassign?: boolean },
): Promise<any> {
    const body: Record<string, unknown> = { job_ids: jobIds };
    if (patientId) body.patient_id = patientId;
    if (newSubjectId) body.new_subject_id = newSubjectId;
    if (options?.forceReassign) body.force_reassign = true;
    return _callAuthenticatedAPI('scan/jobs/bulk-assign-patient/', 'POST', body);
}

export function ingestZip(s3Key: string): Promise<any> {
    return _callAuthenticatedAPI('scan/ingest-zip', 'POST', { s3_key: s3Key });
}

export function getIngestionStatus(ingestionId: string): Promise<any> {
    return _callAuthenticatedAPI(`scan/ingestion/${ingestionId}/status/`, 'GET');
}

export function retryJob(jobId: string): Promise<any> {
    return _callAuthenticatedAPI(`scan/jobs/${jobId}/retry/`, 'POST');
}

export function getJobResult(jobId: string): Promise<any> {
    return _callAuthenticatedAPI(`scan/jobs/${jobId}/result/`, 'GET');
}

type MethodType = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS' | 'HEAD';

function _callAuthenticatedAPI(
    endpoint: string,
    method: MethodType = 'GET',
    body?: { [key: string]: any },
    abortable: boolean = false
): Promise<any> {
    const headers = {
        [AUTHORIZATION_HEADER_NAME]: `Bearer ${getAccessToken()}`
    };

    return _callAPI(endpoint, method, body, headers, abortable);
}

async function _refreshAccessToken(): Promise<boolean> {
    const refresh = getRefreshToken();
    if (!refresh) return false;

    try {
        const url = `${BASE_URL.replace(/\/+$/, '')}/user/token/refresh`;
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh }),
        });

        if (!response.ok) return false;

        const data = await response.json();
        if (data.access) {
            setAuthTokens(data.access, data.refresh || refresh);
            return true;
        }
        return false;
    } catch {
        return false;
    }
}

function _callAPI(
    endpoint: string,
    method: MethodType = 'GET',
    body?: { [key: string]: any },
    headers?: { [key: string]: string },
    abortable: boolean = false,
    isRetry: boolean = false
): Promise<any> {
    const config: {
        method: string;
        headers: { [key: string]: string };
        body?: string;
        signal?: AbortSignal;
    } = {
        method,
        headers: headers || {}
    };

    if (body) {
        config['headers']['Content-Type'] = 'application/json';
        config['body'] = JSON.stringify(body);
    }

    if (abortable) {
        config['signal'] = signal;
    }

    const joinURL = `${BASE_URL.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;

    return fetch(joinURL, config)
        .catch((err) => {
            return Promise.reject({
                status: -1,
                aborted: abortable && err.name === 'AbortError'
            });
        })
        .then((response) =>
            response
                .json()
                .catch((_err) => {
                    return Promise.reject({ status: response.status });
                })
                .then(async (responseBody) => {
                    if (response.ok) {
                        return Promise.resolve(responseBody);
                    }

                    if (
                        response.status === 401 &&
                        !isRetry &&
                        !isRefreshing &&
                        !isRedirecting &&
                        window.location.pathname !== '/'
                    ) {
                        isRefreshing = true;
                        const refreshed = await _refreshAccessToken();
                        isRefreshing = false;

                        if (refreshed) {
                            const retryHeaders = {
                                ...(headers || {}),
                                [AUTHORIZATION_HEADER_NAME]: `Bearer ${getAccessToken()}`
                            };
                            return _callAPI(endpoint, method, body, retryHeaders, abortable, true);
                        }

                        isRedirecting = true;
                        clearAuthTokens();
                        window.location.href = '/';
                    }

                    if (
                        response.status === 401 &&
                        !isRedirecting &&
                        window.location.pathname !== '/'
                    ) {
                        isRedirecting = true;
                        clearAuthTokens();
                        window.location.href = '/';
                    }

                    return Promise.reject({
                        status: response.status,
                        data: responseBody,
                        error: responseBody.error,
                        description: responseBody.description
                    });
                })
        );
}
